import React, { useEffect, useState } from "react";

// COMMON
import { formatDateForInputTypeDate } from "../../common/dateMask";
// SERVICES
import { getDiscountCoupon } from "../../api/discountCoupons";
// APP COMPONENTS
import ModalHeader from "../modals/modalHeader";
import PercentageInput from "../Inputs/percentageInput";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Backdrop from "@mui/material/Backdrop";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
// STYLES
import { styled } from "@mui/material/styles";

const ModalRoot = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  border: "none",
  borderRadius: 12,
  // overflow: 'auto',
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
    height: "95% !important",
    borderRadius: "0 0",
    overflow: "auto",
  },
  [theme.breakpoints.up("md")]: {
    width: "30% !important",
  },
  backgroundColor: "#fff",
  paddingRight: 24,
  paddingLeft: 24,
  paddingBottom: 24,
  paddingTop: 0,
  "&:focus-visible": {
    border: "none",
  },
}));

export default function CreateOrEditDiscountCouponModal({
  discountCoupon,
  show,
  closeModal,
  createDiscountCoupon,
  editDiscountCoupon,
}) {
  const [loading, setLoading] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [code, setCode] = useState("");
  const [value, setValue] = useState(0);

  const [controlByDate, setControlByDate] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [controlMaxUsageCount, setControlMaxUsageCount] = useState(false);
  const [maxUsageCount, setMaxUsageCount] = useState(1);
  const [currentUsageCount, setCurrentUsageCount] = useState(0);

  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (discountCoupon) {
      setIsEditing(true);
      loadData();
    }
  }, []);

  const loadData = async () => {
    try {
      const { data } = await getDiscountCoupon(discountCoupon.id);

      setCode(data.code);
      setValue(data.value);

      // Assuming data.start_date and data.end_date are in a format that JavaScript's Date constructor can parse
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      // Format the dates
      const formattedStartDate = formatDateForInputTypeDate(startDate);
      const formattedEndDate = formatDateForInputTypeDate(endDate);

      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);
      setMaxUsageCount(data.max_usage_count);
      setCurrentUsageCount(data.current_usage_count);
      setIsActive(data.is_active);
    } catch (e) {
      console.log("Erro ao carregar cupom de desconto");
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    setSubmitted(true);

    const isoStartDate = controlByDate
      ? new Date(startDate).toISOString()
      : new Date().toISOString();
    const isoEndDate = controlByDate ? new Date(endDate).toISOString() : null;

    const discountCouponObj = {
      id: discountCoupon?.id || null,
      code: code,
      value: value,
      start_date: isoStartDate,
      end_date: isoEndDate,
      max_usage_count: controlMaxUsageCount ? maxUsageCount : 9999999,
      is_active: isActive,
    };

    if (isEditing) {
      editDiscountCoupon(discountCouponObj);
    } else {
      createDiscountCoupon(discountCouponObj);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <Stack
          spacing={2}
          sx={{
            m: "auto",
          }}
        >
          <CircularProgress sx={{ "&:focus-visible": { border: "none" } }} />
        </Stack>
      </Box>
    );
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={show}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{
        zIndex: 5000,
        "&:focus-visible": {
          border: "none",
        },
      }}
    >
      <Fade in={show}>
        <ModalRoot>
          <ModalHeader
            label={
              discountCoupon
                ? `Cupom: ${discountCoupon.code}`
                : "Cupom de Desconto"
            }
            margin={1}
            onClose={closeModal}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Código"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <PercentageInput
                label="Valor em Porcentagem"
                value={value}
                onChange={setValue}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={controlByDate}
                    onChange={(e) => setControlByDate(e.target.checked)}
                  />
                }
                label="Definir Validade"
              />
            </Grid>
            {controlByDate && (
              <>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    label="Início"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={controlMaxUsageCount}
                    onChange={(e) => setControlMaxUsageCount(e.target.checked)}
                  />
                }
                label={`Definir Limite de Uso - ${currentUsageCount} usos atuais`}
              />
            </Grid>
            {controlMaxUsageCount && (
              <Grid item xs={12}>
                <TextField
                  label="Número Máximo de Utilizações"
                  value={maxUsageCount}
                  onChange={(e) => setMaxUsageCount(e.target.value)}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label={`${isActive ? "DISPONÍVEL" : "INDISPONÍVEL"}`}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                fullWidth
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </ModalRoot>
      </Fade>
    </Modal>
  );
}
